import { Box, Tab, Tabs } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LineChart,
  Line,
  Legend,
} from "recharts";

// -------------------- SAMPLE DATA --------------------
const salesDataDaily = [
  { name: "Mon", sales: 1200, revenue: 5000, orders: 45 },
  { name: "Tue", sales: 1500, revenue: 6200, orders: 50 },
  { name: "Wed", sales: 1700, revenue: 7100, orders: 55 },
  { name: "Thu", sales: 2000, revenue: 9000, orders: 65 },
  { name: "Fri", sales: 2200, revenue: 11000, orders: 70 },
  { name: "Sat", sales: 1800, revenue: 8200, orders: 60 },
  { name: "Sun", sales: 1600, revenue: 7500, orders: 58 },
];

const abandonedCartData = [
  { name: "Mon", abandoned: 5, conversion: 20 },
  { name: "Tue", abandoned: 8, conversion: 18 },
  { name: "Wed", abandoned: 6, conversion: 22 },
  { name: "Thu", abandoned: 9, conversion: 19 },
  { name: "Fri", abandoned: 7, conversion: 21 },
  { name: "Sat", abandoned: 4, conversion: 23 },
  { name: "Sun", abandoned: 3, conversion: 24 },
];

const customerBehaviorData = [
  { segment: "New", aov: 50, frequency: 1, repeatLikelihood: 20 },
  { segment: "Returning", aov: 120, frequency: 3, repeatLikelihood: 60 },
  { segment: "VIP", aov: 350, frequency: 5, repeatLikelihood: 90 },
];

const predictiveData = [
  { product: "Product A", forecast: 150, restockQty: 100 },
  { product: "Product B", forecast: 80, restockQty: 50 },
  { product: "Product C", forecast: 200, restockQty: 180 },
];

const marketData = [
  { category: "Electronics", trendingScore: 80 },
  { category: "Home", trendingScore: 60 },
  { category: "Fitness", trendingScore: 75 },
];

// Additional sample data for Cart Insights
const productsLeftInCart = [
  { name: "Product X", quantity: 5, value: 200 },
  { name: "Product Y", quantity: 3, value: 150 },
  { name: "Product Z", quantity: 8, value: 400 },
];
const revenueData = [
  { name: "Week 1", projected: 3500 },
  { name: "Week 2", projected: 4200 },
  { name: "Week 3", projected: 4800 },
  { name: "Week 4", projected: 5200 },
];
// Customer behavior targeted campaigns
const targetedCampaigns = [
  "Personalized email offers",
  "Loyalty program rewards",
  "Exclusive VIP discounts",
];
interface ForecastItem {
  product: string;
  category: string;
  forecast: number;
}

interface LowProductItem {
  product: string;
  category: string;
  sales: number;
}

interface SalesSpike {
  month: string;
  sales: number;
}
// Product demand forecast data
const demandForecast: ForecastItem[] = [
  { product: "Essential Oil Diffuser", category: "Home", forecast: 100 },
  { product: "Linen Midi Dress", category: "Fashion", forecast: 150 },
  { product: "Noise-Canceling Headphones", category: "Electronics", forecast: 80 },
  { product: "Organic Green Tea", category: "Food & Beverage", forecast: 120 }
];

// Low-performing products list
const lowPerformingProductsData: LowProductItem[] = [
  { product: "Canvas Tote Bag", category: "Fashion", sales: 40 },
  { product: "Ceramic Mug", category: "Home", sales: 32 },
  { product: "Bluetooth Speaker", category: "Electronics", sales: 18 }
];

// Suggested restock quantity
const suggestedRestockQty: number = 120;

// Recommended restock date
const restockingDate: string = "May 10, 2024";

// Predicted sales spike chart data
const salesSpikeData: SalesSpike[] = [
  { month: "Jan", sales: 45 },
  { month: "Feb", sales: 50 },
  { month: "Mar", sales: 65 },
  { month: "Apr", sales: 80 },
  { month: "May", sales: 90 },
  { month: "Jun", sales: 120 },
  { month: "Jul", sales: 150 },
  { month: "Aug", sales: 130 },
  { month: "Sep", sales: 75 },
  { month: "Oct", sales: 95 },
  { month: "Nov", sales: 140 },
  { month: "Dec", sales: 170 }
];

// Predictive analytics extra data
const lowPerformingProducts = ["Product D", "Product E"];
const seasonalTrendAlerts = ["Increase promotions in December", "Restock summer items in April"];
const trendingProducts = [
  { name: 'Embroidery Kaftan', trend: 'Up' },
  { name: "Men's Sneakers", trend: 'Up' },
  { name: 'Abaya', trend: 'Up' },
  { name: 'Leather Handbag', trend: 'Up' },
];

const competitorListings = [
  { category: 'Kaftan', price: '200', promotion: '25% OFF' },
  { category: 'Handbag', price: '375', promotion: 'Clearance' },
  { category: 'Sports Watch', price: '150', promotion: '12% OFF' },
];

const productExpansion = [
  'Track Pants',
  'Custom Jewelry',
  'Natural Soap',
];

const marketGapInsights = [
  'Eco-Friendly Skincare',
  'Handcrafted Home Decor',
  'Fitness Accessories',
];

const promotionChartData = [
  { name: 'Flash Sale', suggested: 6 },
  { name: 'Buy 1 Get 1', suggested: 8 },
  { name: '10% Off', suggested: 6 },
  { name: '20% Off', suggested: 4 },
  { name: 'Low Off', suggested: 3 },
];
// AI Market Intelligence extra info


const RevenueTabs = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Dummy time period selector (not functional, placeholder)
  const timePeriods = ["Daily", "Weekly", "Monthly", "Custom"];
  const [selectedPeriod, setSelectedPeriod] = useState("Daily");

  return (
    <>
      <div className="main_title">
        <h1>Revenue Analytics</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Revenue Analytics
        </p>
      </div>

      {/* Tabs */}
      <Box className="custom_tabs1">
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          className="custom_tabs_links"
        >
          <Tab label="Sales Trends & Revenue" />
          <Tab label="Cart Insights" />
          <Tab label="Customer Behavior Analysis" />
          <Tab label="Predictive Analytics" />
          <Tab label="AI Market Intelligence" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <div style={{ marginTop: 20 }} className="cards revenue_mod">
        {/* ------------------ SALES TRENDS & REVENUE ------------------ */}
        {tab === 0 && (
          <>
            <div className="cards_header">
              <div className="left_s">
                <h2>Time Period:</h2>
              </div>
              <div className="right_s form">
                <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
                  {timePeriods.map((period) => (
                    <option key={period} value={period}>{period}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sales Trends Card */}
            <div className="card">
              <div className="card_header">
                <div className="card_title">
                  <h3>Sales Trends</h3>
                </div>
                <div className="card_subtitle">
                  <span className="percentage-change">+12%</span>
                </div>
              </div>

              <div className="kpi">
                <div className="kpi_item"><h4>GMV</h4><p>+12%</p></div>
                <div className="kpi_item"><h4>Orders</h4><p>+7%</p></div>
                <div className="kpi_item"><h4>Views</h4><p>+4%</p></div>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesDataDaily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sales" stroke="#2d8ee3" />
                  <Line type="monotone" dataKey="revenue" stroke="#d32f2f" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Revenue Projections Card */}
            <div className="card">
              <div className="card_header">
                <div className="card_title">
                  <h3>Revenue Projections</h3>
                </div>
              </div>

              <div className="revenue_projections">
                <div className="projection_item">
                  <h4>Next 7 Days Projection</h4>
                  <p>AED 4,820</p>
                </div>
                <div className="projection_item">
                  <h4>Next 30 Days Projection</h4>
                  <p>AED 21,350</p>
                </div>
              </div>

              {/* ⭐ Added Revenue Projection Line Chart */}
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="projected" stroke="#ffa726" name="Projected Revenue" />
                </LineChart>
              </ResponsiveContainer>

              <div className="kpi">
                <div className="kpi_item">
                  <h4>High Engagement Expected</h4>
                  <p>Higher customer contribution expected.</p>
                </div>
                <div className="kpi_item">
                  <h4>Stable Repeat Customer Contribution</h4>
                </div>
                <div className="kpi_item">
                  <h4>Category Seasonal Trends</h4>
                  <p>Mid growth expected.</p>
                </div>
              </div>
            </div>

          </>
        )}

        {/* ------------------ CART INSIGHTS ------------------ */}
        {tab === 1 && (
          <>
            <div className="bottom_info_grid">
              <div className="info_card"><h4>Total Abandoned Carts</h4><p>42</p></div>
              <div className="info_card"><h4>Abandonment Rate</h4><p>18%</p></div>
              <div className="info_card"><h4>Conversion Rate</h4><p>82%</p></div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={abandonedCartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="abandoned" fill="#D3B88C" name="Abandoned Carts" />
                <Bar dataKey="conversion" fill="#2d8ee3" name="Conversion Rate (%)" />
              </BarChart>
            </ResponsiveContainer>

            <div className="bottom_info_grid" style={{ marginTop: 15 }}>
              <div className="info_card">
                <h4>Products Frequently Left in Cart</h4>
                <ul>
                  {productsLeftInCart.map((p) => <li key={p.name}>{p.name} - Qty: {p.quantity}, Value: ${p.value}</li>)}
                </ul>
              </div>
              <div className="info_card">
                <h4>AI Suggestions for Cart Recovery</h4>
                <ul>
                  <li>Discounts offered</li>
                  <li>Promotional campaigns</li>
                  <li>Free shipping notifications</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* ------------------ CUSTOMER BEHAVIOR ------------------ */}
        {tab === 2 && (
          <>

            {/* --- KPI GRID --- */}
            <div className="kpi_grid">
              <div className="kpi_card">
                <h4>Average Order Value (AOV)</h4>
                <p className="kpi_value">AED 350</p>
              </div>

              <div className="kpi_card">
                <h4>Purchase Frequency</h4>
                <p className="kpi_value">2.7 <span className="unit">per Customer</span></p>
              </div>

              <div className="kpi_card">
                <h4>Repeat Purchase Likelihood</h4>
                <p className="kpi_value">45% <span className="unit">(AI-calculated)</span></p>
              </div>
            </div>

            {/* --- CUSTOMER SEGMENTS --- */}
            <div className="segment_card">
              <h3>Customer Segments</h3>

              <div className="segment_row">
                <span>New</span>
                <div className="segment_bar">
                  <div className="fill new" style={{ width: "65%" }}></div>
                </div>
                <span className="count">524</span>
              </div>

              <div className="segment_row">
                <span>Returning</span>
                <div className="segment_bar">
                  <div className="fill returning" style={{ width: "50%" }}></div>
                </div>
                <span className="count">408</span>
              </div>

              <div className="segment_row">
                <span>VIP</span>
                <div className="segment_bar">
                  <div className="fill vip" style={{ width: "15%" }}></div>
                </div>
                <span className="count">56</span>
              </div>
            </div>

            {/* --- SUGGESTED CAMPAIGNS --- */}
            <div className="campaign_card">
              <h3>Suggested Targeted Campaigns</h3>

              <ul>
                {targetedCampaigns.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>


          </>
        )}

        {/* ------------------ PREDICTIVE ANALYTICS ------------------ */}
        {tab === 3 && (
          <>
            {/* --- PRODUCT DEMAND FORECAST --- */}
            <div className="predictive_grid">

              <div className="card">
                <h3>Product Demand Forecast</h3>
                <div className="forecast_table">
                  {demandForecast.map((item) => (
                    <div className="row" key={item.product}>
                      <span className="product">{item.product}</span>
                      <span className="category">{item.category}</span>
                      <span className="forecast">{item.forecast}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* --- LOW-PERFORMING PRODUCTS --- */}
              <div className="card">
                <h3>Low-Performing Products List</h3>
                <div className="forecast_table">
                  {lowPerformingProductsData.map((p) => (
                    <div className="row" key={p.product}>
                      <span className="product">{p.product}</span>
                      <span className="category">{p.category}</span>
                      <span className="forecast">{p.sales}</span>
                    </div>
                  ))}
                </div>

                <div className="suggest_box">
                  <h4>Suggested Restock Quantity</h4>
                  <p className="qty">{suggestedRestockQty} Units</p>
                </div>
              </div>

              {/* --- RESTOCK DATE & SUGGESTED QTY --- */}
              <div className="card small_card">
                <h3>Recommended Restocking Date</h3>
                <p className="date_text">{restockingDate}</p>

                <h3 style={{ marginTop: 20 }}>Suggested Restock Quantity</h3>
                <p className="qty_text">{suggestedRestockQty} Units</p>
              </div>

              {/* --- SALES SPIKE PERIODS (BAR CHART) --- */}
              <div className="card">
                <h3>Predicted Sales Spike Periods</h3>

                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={salesSpikeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="sales" fill="#c79845" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* --- SEASONAL ALERTS --- */}
              <div className="card alerts_card">
                <h3>Seasonal Trends & Alerts</h3>
                <ul className="alerts_list">
                  {seasonalTrendAlerts.map((alert, i) => (
                    <li key={i}>
                      <span className="dot"></span> {alert}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>

        )}

        {/* ------------------ AI MARKET INTELLIGENCE ------------------ */}
        {tab === 4 && (
          <>
            <div className="market-dashboard">
              <div className="dashboard-row top-row">

                {/* Trending Products / Categories */}
                <div className="info-section trending-products">
                  <h3 className="section-title">Trending Products / Categories</h3>
                  <ul>
                    {trendingProducts.map((item, index) => (
                      <li key={index}>
                        <span className={`trend-indicator ${item.trend.toLowerCase()}`}>
                          {/* Assuming an arrow icon or character here */}
                          {item.trend === 'Up' ? '⬆️' : '⬇️'}
                        </span>
                        {item.name}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Competitor Product Listings */}
                <div className="info-section competitor-listings">
                  <h3 className="section-title">Competitor Product Listings</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Promotion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitorListings.map((item, index) => (
                        <tr key={index}>
                          <td>{item.category}</td>
                          <td>AED {item.price}</td>
                          <td className="promotion">{item.promotion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Market Gap Insights (Top Right) */}
                <div className="info-section market-gap-insights top-gap">
                  <h3 className="section-title">Market Gap Insights</h3>
                  <p className="subtitle">Niche Opportunities Identified by AI</p>
                  <ul>
                    {marketGapInsights.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* -------------------- Bottom Section -------------------- */}
              <div className="dashboard-row bottom-row">

                {/* Suggested Product Expansion Opportunities */}
                <div className="info-section product-expansion">
                  <h3 className="section-title">Suggested Product Expansion Opportunities</h3>
                  <ul>
                    {productExpansion.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* Promotion/Discount Recommendations (Chart) */}
                <div className="info-section promotion-chart">
                  <h3 className="section-title">Promotion/Discount Recommendations</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={promotionChartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} style={{ fontSize: '10px' }} />
                      <YAxis domain={[0, 10]} tickCount={3} tickLine={false} axisLine={false} style={{ fontSize: '10px' }} label={{ value: 'Suggested Promotions', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '12px' } }} />
                      <Tooltip />
                      <Bar dataKey="suggested" fill="#ffb400" radius={[5, 5, 0, 0]} name="Suggested Promotions" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Market Gap Insights (Bottom Right - Duplicated as per screenshot) */}
                <div className="info-section market-gap-insights bottom-gap">
                  <h3 className="section-title">Market Gap Insights</h3>
                  <div className="bulb-icon">💡</div>
                  <p className="subtitle">Niche Opportunities Identified by AI</p>
                  <ul>
                    {marketGapInsights.slice(0, 2).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>




          </>
        )}
      </div>
    </>
  );
}

export default RevenueTabs;
